import { Box, CircularProgress, Switch, IconButton, Checkbox } from '@mui/material';
import { Typography, useMediaQuery } from '@mui/material';
import { ProjectsType } from './types';
import { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { AuthContext, UserDetailsContextInterface } from '../../contexts';
import { AuthContextInterface, UserDetailsContext } from '../../contexts';
import { styles } from './styles';
import { getAllProjects, updateProject } from '../../services/projectService';
import useGetValidAccessToken from '../../hooks/useGetValidAccessToken';
import { GetEnterpriseAllUsersResponse, Role, User } from '../../proto/api_pb';
import { FileState, GetProjectsResponse, Project, UpdateProjectResponse } from '../../proto/api_pb';
import { AddProjectModal } from '../../components/Modal/AddProjectModal';
import { SingleProject } from '../../components/SingleProject/SingleProject';
import { ProjectSearchBar } from '../../components/SingleProject/ProjectSearchBar';
import { ProjectsFilterMenu } from './ProjectsFilterMenu';
import { ProjectsSortMenu } from './ProjectsSortMenu';
import { ContainerForGrid } from '../../components/Exolore/styles';
import { MobileScreenBottomNavigationContainer } from '../Tracks/styles';
import { MobileScreenBottomNavigation } from '../../components/TracksPageComponents/MobileScreenBottomNavigation';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { getEnterpriseAllUsers } from '../../services/enterpriseService';
import { MobileDrawer } from '../../components/MobileDrawer/MobileDrawer';
import { useNavigate, useLocation } from 'react-router-dom';
import { styles as tracksStyles } from '../Tracks/styles';
import { ShareModal } from '../../components/Modal/ShareModal';
import BatchEditProjectModal from '../../components/Modal/BatchEditProjectModal';
import { THEME, APP_COLOR } from '../../theme';
import { FilterAndSortButton } from '../../components/TracksPageComponents/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomButton from '../../components/CustomButton/CustomButton';
import DeleteConfirmationModal from '../../components/Modal/DeleteConfirmationModal';
import { FileStateReverseMap } from '../../components/SingleProject/types';
import { toast } from 'react-toastify';
const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

export const Projects: React.FC<ProjectsType> = ({
  session,
  setInbox,
  openFoldersList,
  setOpenFoldersList,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [queryValue, setQueryValue] = useState<string>('');
  const [userNames, setUserNames] = useState<string[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>();
  const [sortBy, setSortBy] = useState<string>('Last modified');
  const [isReorderAvailable, setIsReorderAvailable] = useState<boolean>(false);
  const [draggedProjectIndex, setDraggedProjectIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [customOrder, setCustomOrder] = useState<Map<string, number>>(new Map());
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [isShareMode, setIsShareMode] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [isBatchDeleteConfirmOpen, setIsBatchDeleteConfirmOpen] = useState<boolean>(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState<boolean>(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const isMobileScreen = useMediaQuery('(max-width:900px)');
  const { page, scrollContainerRef, setIsScrollEnabled, setPage } = useInfiniteScroll();
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [lastResetKey, setLastResetKey] = useState<string | null>(null);
  // State to track if we should show the custom order even if reordering is disabled
  const [shouldShowCustomOrder, setShouldShowCustomOrder] = useState<boolean>(false);

  useEffect(() => {
    if (!enterprise?.id || !userId) return;
    const savedShowCustomOrder = localStorage.getItem(
      `showCustomOrder_${enterprise?.id}_${userId}`
    );
    if (savedShowCustomOrder === 'true') {
      setShouldShowCustomOrder(true);
    }
  }, [enterprise?.id, userId]);

  // Removed the auto-save useEffect to prevent overwriting on initial load

  const handleUseEffect = useCallback(
    async (refresh: boolean = false) => {
      if (!userId || !enterprise?.id || !enterprise.role) return;
      setIsLoading(true);

      if (refresh) {
        setPage(0);
        setProjects([]);
      }

      const token = await getValidAccessToken();
      // Use custom order if reordering is ON or if we should show custom order
      const useCustomOrder = isReorderAvailable || shouldShowCustomOrder;
      const actualSortBy = useCustomOrder ? 'Custom Order' : sortBy;
      const sortOrder = actualSortBy === 'Title' ? 'asc' : 'desc';
      const res = await getAllProjects(
        token,
        userId,
        queryValue,
        selectedCollaborators,
        enterprise?.id ?? '',
        page,
        Number(enterprise?.role) ?? 0,
        actualSortBy,
        sortOrder
      );

      if (res instanceof GetProjectsResponse) {
        let projectsList = res.getProjectsList();

        if (useCustomOrder && customOrder.size > 0) {
          projectsList = [...projectsList].sort((a, b) => {
            const posA = customOrder.get(a.getId() || '') ?? Infinity;
            const posB = customOrder.get(b.getId() || '') ?? Infinity;
            if (posA === Infinity && posB === Infinity) return 0;
            if (posA === Infinity) return 1;
            if (posB === Infinity) return -1;
            return posA - posB;
          });
        }

        if (refresh) {
          setProjects(projectsList);
          setSelectedProjects(new Set());
        } else {
          setProjects((prev) => {
            const existingProjectIds = new Set(prev.map((project) => project.getId()));
            const uniqueProjects = projectsList.filter(
              (project) => !existingProjectIds.has(project.getId())
            );
            return [...prev, ...uniqueProjects];
          });
        }

        if (res.getProjectsList().length < 8 && setIsScrollEnabled) {
          setIsScrollEnabled(false);
          if (setIsLoading) setIsLoading(false);
        } else if (setIsScrollEnabled) {
          setIsScrollEnabled(true);
        }
      }
      setIsLoading(false);
    },
    [
      enterprise?.id,
      enterprise?.role,
      getValidAccessToken,
      page,
      queryValue,
      selectedCollaborators,
      setIsScrollEnabled,
      setPage,
      userId,
      sortBy,
      customOrder,
      isReorderAvailable,
      shouldShowCustomOrder,
    ]
  );

  useEffect(() => {
    if (!enterprise?.id || !userId) return;
    const savedOrder = localStorage.getItem(`projectCustomOrder_${enterprise?.id}_${userId}`);
    if (savedOrder) {
      try {
        const orderMap = new Map<string, number>(JSON.parse(savedOrder));
        setCustomOrder(orderMap);
      } catch (e) {
        console.error('Failed to load custom order:', e);
      }
    }
  }, [enterprise?.id, userId]);

  useEffect(() => {
    handleUseEffect();
  }, [handleUseEffect]);

  // Memoize the projects list to ensure it respects the custom order view preference
  const sortedProjects = useMemo(() => {
    if ((isReorderAvailable || shouldShowCustomOrder) && customOrder.size > 0) {
      return [...projects].sort((a, b) => {
        const posA = customOrder.get(a.getId() || '') ?? Infinity;
        const posB = customOrder.get(b.getId() || '') ?? Infinity;
        if (posA === Infinity && posB === Infinity) return 0;
        if (posA === Infinity) return 1;
        if (posB === Infinity) return -1;
        return posA - posB;
      });
    }
    return projects;
  }, [projects, customOrder, isReorderAvailable, shouldShowCustomOrder]);

  const handleDragStart = (index: number) => {
    setDraggedProjectIndex(index);
  };

  const handleDrop = async () => {
    if (isReorderAvailable) {
      if (draggedProjectIndex === null || draggedIndex === null) return;
      // operate on sortedProjects to ensure we are moving the item the user sees
      const updatedProjects = [...sortedProjects];
      const [draggedProject] = updatedProjects.splice(draggedProjectIndex, 1);
      updatedProjects.splice(draggedIndex, 0, draggedProject);
      setProjects([]);
      setDraggedProjectIndex(null);

      const newOrder = new Map<string, number>();
      updatedProjects.forEach((project, index) => {
        const projectId = project.getId();
        if (projectId) {
          newOrder.set(projectId, index);
        }
      });

      setCustomOrder(newOrder);
      const orderArray = Array.from(newOrder.entries());
      localStorage.setItem(
        `projectCustomOrder_${enterprise?.id}_${userId}`,
        JSON.stringify(orderArray)
      );

      setProjects(updatedProjects);
    }
  };

  useEffect(() => {
    const state = location.state as { resetProjects?: boolean } | null;
    if (state?.resetProjects && location.key !== lastResetKey) {
      setQueryValue('');
      setSelectedCollaborators(undefined);
      setSortBy('Last modified');
      setShouldShowCustomOrder(false);
      setIsShareMode(false);
      setIsDeleteMode(false);
      setIsEditMode(false);
      setSelectedProjects(new Set());
      setLastResetKey(location.key);
      handleUseEffect(true);
    }
  }, [location, lastResetKey, handleUseEffect]);

  const handleCheckedProjectChange = (projectId: string) => {
    setSelectedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(projects.map((p) => p.getId()).filter(Boolean));
      setSelectedProjects(allIds);
    } else {
      setSelectedProjects(new Set());
    }
  };

  const isAllSelected = projects.length > 0 && selectedProjects.size === projects.length;
  const isIndeterminate = selectedProjects.size > 0 && selectedProjects.size < projects.length;

  const handleBulkDeleteProjects = useCallback(async () => {
    if (selectedProjects.size === 0) return;
    setIsBatchDeleting(true);
    const token = await getValidAccessToken();
    const projectIds = Array.from(selectedProjects);
    let failures = 0;

    for (const pId of projectIds) {
      const project = projects.find((p) => p.getId() === pId);
      if (!project) {
        failures++;
        continue;
      }
      try {
        const p = new Project();
        p.setId(project.getId());
        p.setOwnerId(project.getOwnerId());
        p.setPrivate(project.getPrivate());
        p.setName(project.getName());
        p.setDescription(project.getDescription());
        p.setArtworkUrl(project.getArtworkUrl());
        p.setProjectState(FileStateReverseMap[FileState.FILE_STATE_DELETED]);
        p.setModifiedTimestamp(Date.now());
        p.setCollaboratorsList(project.getCollaboratorsList());
        p.setTimestamp(project.getTimestamp());
        p.setEnterpriseId(enterprise?.id as string);
        const res = await updateProject(token, p);
        if (!(res instanceof UpdateProjectResponse)) failures++;
      } catch (error) {
        console.error(`Failed to delete project ${pId}:`, error);
        failures++;
      }
    }

    if (failures === 0) {
      toast.success(
        `Successfully deleted ${projectIds.length} project${projectIds.length !== 1 ? 's' : ''}`
      );
    } else {
      toast.warn(
        `Deleted ${projectIds.length - failures} of ${projectIds.length} projects. ${failures} failed.`
      );
    }

    setIsBatchDeleteConfirmOpen(false);
    setIsBatchDeleting(false);
    setIsDeleteMode(false);
    setSelectedProjects(new Set());
    handleUseEffect(true);
  }, [selectedProjects, projects, getValidAccessToken, enterprise, handleUseEffect]);

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? '');
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter((user) => !user.getUsername()?.includes('dummy_ommuse'))
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setEnterpriseUsers(
          res.getUsersList().filter((user) => !user.getUsername()?.includes('dummy_ommuse'))
        );
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken]);

  if (!isEnterpriseAccount) {
    navigate('not-found');
    return <></>;
  }
  return (
    <>
      <MobileDrawer />
      <Box sx={{ height: '100vh', display: 'flex' }}>
        <Box ref={scrollContainerRef} sx={styles.rightSideContainer}>
          <Box
            sx={{
              ...styles.projectsHeaderContainer,
              width: isMobileScreen ? '85vw' : '100%',
            }}
          >
            <Box sx={styles.headerContainer}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '30px' }}>Projects</Typography>
                {isShareMode && selectedProjects.size > 0 && (
                  <Typography sx={{ color: 'text.secondary' }}>
                    ({selectedProjects.size} selected)
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {(Number(enterprise?.role) === Role.ROLE_ADMIN ||
                  Number(enterprise?.role) === Role.ROLE_MANAGER) && (
                  <AddProjectModal
                    enterpriseUsers={enterpriseUsers}
                    handleUseEffect={handleUseEffect}
                    setProjects={setProjects}
                    projects={projects}
                    setPage={setPage}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '10px',
                marginTop: '10px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <ProjectSearchBar
                queryValue={queryValue}
                setQueryValue={setQueryValue}
                setPage={setPage}
                setProjects={setProjects}
              />
              {projects.length > 0 && !isShareMode && !isEditMode && !isDeleteMode && (
                <FilterAndSortButton
                  onClick={() => {
                    setIsShareMode(true);
                  }}
                  sx={{
                    height: '55px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '20px',
                  }}
                >
                  <img
                    style={{
                      width: '17px',
                      height: '17px',
                      marginRight: '8px',
                      filter: THEME === 'light' ? 'invert(100%)' : 'invert(0%) contrast(50%)',
                    }}
                    src="/assets/icons/TracksPageIcons/share-07.svg"
                    alt="Share Project"
                  />
                  Share Project
                </FilterAndSortButton>
              )}
              {projects.length > 0 && isShareMode && (
                <>
                  <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                        Select All
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => {
                        setIsShareMode(false);
                        setSelectedProjects(new Set());
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>Cancel</Typography>
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        if (selectedProjects.size > 0 || projects.length > 0) {
                          setIsShareModalOpen(true);
                        }
                      }}
                      disabled={selectedProjects.size === 0 && projects.length === 0}
                      sx={{
                        color: APP_COLOR,
                        '&:disabled': {
                          color: 'text.disabled',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>Share</Typography>
                    </IconButton>
                  </Box>
                  <ShareModal
                    id={
                      selectedProjects.size > 0
                        ? Array.from(selectedProjects).join(',')
                        : projects
                            .map((p) => p.getId())
                            .filter(Boolean)
                            .join(',')
                    }
                    title={
                      selectedProjects.size > 0
                        ? `${selectedProjects.size} Selected Projects`
                        : `${enterprise?.name || 'Enterprise'} Projects`
                    }
                    shareType={5 as any}
                    open={isShareModalOpen}
                    onOpenChange={setIsShareModalOpen}
                    onClose={() => {
                      setIsShareMode(false);
                      setSelectedProjects(new Set());
                    }}
                  />
                </>
              )}
              <ProjectsFilterMenu
                userNames={userNames}
                setSelectedCollaborators={setSelectedCollaborators}
                setProjects={setProjects}
                setPage={setPage}
                selectedCollaborators={selectedCollaborators}
              />

              <ProjectsSortMenu
                setProjects={setProjects}
                setPage={setPage}
                sortBy={sortBy}
                setSortBy={(newSortBy) => {
                  setSortBy(newSortBy);
                  setShouldShowCustomOrder(false);
                  localStorage.setItem(`showCustomOrder_${enterprise?.id}_${userId}`, 'false');
                }}
              />
              {projects.length > 0 && !isEditMode && !isShareMode && !isDeleteMode && (
                <>
                  {Number(enterprise?.role) === Role.ROLE_ADMIN && (
                    <CustomButton
                      icon={EditIcon}
                      text="Edit"
                      onClick={() => {
                        setIsEditMode(true);
                        setSelectedProjects(new Set());
                      }}
                    />
                  )}
                  {Number(enterprise?.role) === Role.ROLE_ADMIN && (
                    <CustomButton
                      icon={DeleteIcon}
                      text="Delete"
                      onClick={() => {
                        setIsDeleteMode(true);
                        setSelectedProjects(new Set());
                      }}
                    />
                  )}
                </>
              )}
              {projects.length > 0 && isEditMode && (
                <>
                  <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                        Select All
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => {
                        setIsEditMode(false);
                        setSelectedProjects(new Set());
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>Cancel</Typography>
                    </IconButton>
                    <CustomButton
                      text={`Edit${selectedProjects.size > 0 ? ` (${selectedProjects.size})` : ''}`}
                      onClick={() => {
                        if (selectedProjects.size > 0) {
                          setIsEditModalOpen(true);
                        }
                      }}
                      isDisabled={selectedProjects.size === 0}
                      customStyles={{
                        height: '40px',
                        color: 'black',
                        '&.Mui-disabled': { color: 'black', opacity: 0.4 },
                      }}
                    />
                  </Box>
                </>
              )}
              {projects.length > 0 && isDeleteMode && (
                <>
                  <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                        Select All
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => {
                        setIsDeleteMode(false);
                        setSelectedProjects(new Set());
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>Cancel</Typography>
                    </IconButton>
                    <CustomButton
                      text={`Delete${selectedProjects.size > 0 ? ` (${selectedProjects.size})` : ''}`}
                      onClick={() => {
                        if (selectedProjects.size > 0) {
                          setIsBatchDeleteConfirmOpen(true);
                        }
                      }}
                      isDisabled={selectedProjects.size === 0}
                      customStyles={{
                        height: '40px',
                        color: 'black',
                        '&.Mui-disabled': { color: 'black', opacity: 0.4 },
                      }}
                    />
                  </Box>
                </>
              )}
              <Box sx={tracksStyles.reorderContainer}>
                <Switch
                  {...label}
                  checked={isReorderAvailable}
                  onChange={() => {
                    if (!isReorderAvailable) {
                      setPage(0);
                      setProjects([]);
                      setIsReorderAvailable(true);
                      setShouldShowCustomOrder(true);
                      localStorage.setItem(`showCustomOrder_${enterprise?.id}_${userId}`, 'true');
                    } else {
                      setPage(0);
                      setProjects([]);
                      setIsReorderAvailable(false);
                      // Custom order remains shown (shouldShowCustomOrder remains true)
                    }
                  }}
                />
                <Typography sx={{ ml: 1, fontSize: '14px' }}>Custom Order</Typography>
              </Box>
            </Box>
          </Box>
          <ContainerForGrid sx={isMobileScreen ? { marginBottom: '90px' } : { marginTop: '15px' }}>
            {sortedProjects.map((project, index) => {
              const projectId = project.getId();

              let dragStyle = {};
              if (isReorderAvailable && draggedProjectIndex !== null && draggedIndex !== null) {
                if (index === draggedProjectIndex) {
                  dragStyle = {
                    transform: 'scale(1.02)',
                    opacity: 0.95,
                    zIndex: 5,
                  };
                } else if (index === draggedIndex) {
                  dragStyle =
                    draggedIndex > draggedProjectIndex
                      ? { transform: 'translateX(-110%)' }
                      : { transform: 'translateX(110%)' };
                }
              }

              return (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    transition: 'transform 0.15s ease',
                    ...dragStyle,
                  }}
                >
                  {(isShareMode || isEditMode || isDeleteMode) && (
                    <Checkbox
                      checked={projectId ? selectedProjects.has(projectId) : false}
                      onChange={() => projectId && handleCheckedProjectChange(projectId)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 10,
                        color: 'white',
                        '&.Mui-checked': {
                          color: APP_COLOR,
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 28,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '4px',
                        },
                      }}
                    />
                  )}
                  <SingleProject
                    index={index}
                    project={project}
                    session={session}
                    setInbox={setInbox}
                    handleUseEffect={handleUseEffect}
                    openFoldersList={openFoldersList}
                    setOpenFoldersList={setOpenFoldersList}
                    isReorderAvailable={isReorderAvailable}
                    handleDragStart={handleDragStart}
                    handleDrop={handleDrop}
                    setDraggedIndex={setDraggedIndex}
                  />
                </Box>
              );
            })}
          </ContainerForGrid>
          {isLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          )}
          {projects.length === 0 && !isLoading && (
            <Box sx={styles.emptyMessageContainer}>
              <Typography>No projects found!</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
      <BatchEditProjectModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setIsEditMode(false);
          setSelectedProjects(new Set());
        }}
        selectedProjectIds={selectedProjects}
        projects={projects}
        onSuccess={() => handleUseEffect(true)}
      />
      <DeleteConfirmationModal
        isMessageModalOpen={isBatchDeleteConfirmOpen}
        message={`Are you sure you want to delete ${selectedProjects.size} project${selectedProjects.size !== 1 ? 's' : ''}?`}
        toggle={() => setIsBatchDeleteConfirmOpen(false)}
        handleConfirm={handleBulkDeleteProjects}
        loading={isBatchDeleting}
      />
    </>
  );
};
